import React, { useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";

import {
  addNewProduct,
  addProductBenefit,
  addProductAdditionalInfo,
  addProductEligibility,
  addProductPremium,
  updateProduct,
  updateProductBenefit,
  updateProductAdditionalInfo,
  updateProductEligibility,
} from "services/insuranceService";
import BasicInfoForm from "./BasicInfoForm";
import KeyBenefitsForm from "./KeyBenefitsForm";
import FeaturesForm from "./FeaturesForm";
import EligibilityForm from "./EligibilityForm";
import PremiumForm from "./PremiumForm";
import { fetchProductsThunk } from "slices/Insurance/thunk";
import { useDispatch } from "react-redux";

interface ProductFormProps {
  validation: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  toggleModal: () => void;
  isNewProduct: boolean;
}

const TABS = ["Basic Info", "Key Benefits", "Features", "Eligibility", "Premium"];
const TAB_REQUIRED_FIELDS: Record<string, string[]> = {
  "Basic Info": [
    "product_category",
    "product_name",
    "product_code",
    "plan_type",
    "product_description",
    "product_image_url",
  ],

  "Key Benefits": [
    "benefits"  // Must contain at least 1 saved item
  ],

  "Features": [
    "policy_term",
    "premium_term",
    "minimum_sum_assured",
    "maximum_sum_assured",
    "maturity_bonus_benefit",
    "death_benefit",
    "loan_facility",
    "surrendar_value", 
  ],

  "Eligibility": [
    "min_age",
    "max_age",
    "max_maturity_age",
    "customer_eligibility",
    "medical_requirement",
  ],

  "Premium": [
    "premium_note",
    // "premium_table"  <-- Handled separately by your isPremiumValid()
  ],
};

const ProductForm: React.FC<ProductFormProps> = ({
  validation,
  activeTab,
  setActiveTab,
  toggleModal,
  isNewProduct,
}) => {
  const values = validation.values;
  const isEdit = !!values.id;
  const allowDelete = !isNewProduct && isEdit;
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const handleStepSubmit = async () => {
    setLoading(true); 
    const requiredForThisTab = TAB_REQUIRED_FIELDS[activeTab] || [];
    // Validate entire form, but gate progression based only on current tab fields
    const errors = await validation.validateForm();

    // Mark only current tab fields as touched so their messages show
    requiredForThisTab.forEach((field) => {
      validation.setFieldTouched(field, true, false);
    });

    // Helper: is the value missing/blank?
    const isEmpty = (val: any) =>
      val === undefined ||
      val === null ||
      (typeof val === "string" && val.trim() === "") ||
      (Array.isArray(val) && val.length === 0);

    const missingRequired = requiredForThisTab.some((field) => isEmpty(values[field]));

    const tabErrors = Object.keys(errors).filter((key) =>
      requiredForThisTab.some(
        (field) => key === field || key.startsWith(`${field}[`)
      )
    );

    const hasTabErrors = missingRequired || tabErrors.length > 0;

    if (hasTabErrors) {
      toast.error("Please fill required fields on this step before proceeding.");
      setLoading(false);
      return;   
    }
    const withStatus = (payload: any) => ({
      ...payload,
      status: values.status,
    });

    try {
      switch (activeTab) {
        case "Basic Info": {
          const payload = {
            product_category: values.product_category,
            product_name: values.product_name,
            product_code: values.product_code,
            product_description: values.product_description,
            product_image_url: values.product_image_url,
            plan_type: values.plan_type,
          };

          if (isEdit) {
            try {
              await updateProduct(values.id, withStatus(payload));
              validation.setFieldValue("id", values.id, false);
              validation.setFieldValue("product_id", values.id, false);
              toast.success("Basic Info Updated!");
            } catch (error: any) {
              console.error("Update failed:", error);
              const backendMsg =
                error?.response?.data?.message ||
                error?.response?.data?.error;

              toast.error(backendMsg || "Unable to update record.");
              setLoading(false); 
              return;
            }
          } else {
            const response = await addNewProduct(payload);
            const productId = response?.data;
            if (productId) {
              validation.setFieldValue("id", productId, false);
              validation.setFieldValue("product_id", productId, false); 
              toast.success("Basic Info Saved!");
            } else {
              toast.error("Product ID not returned from API!");
              setLoading(false);
              return;
            }
          }
          setLoading(false);
          break;
        }

       case "Key Benefits": {
          if (!values.benefits?.length) {
            toast.error("Please add at least one benefit");
            setLoading(false);
            return;
          }

          const hasSavedBenefit = values.benefits.some((b: any) => {
            return b.id || b.saved === true;         
          });

          if (!hasSavedBenefit) {
            toast.error("Please Save at least one benefit before proceeding.");
            setLoading(false);
            return;
          }

          break;
        }



        case "Features": {
          const payload = {
            product_id: values.id,
            policy_term: values.policy_term,
            premium_term: values.premium_term,
            minimum_sum_assured: values.minimum_sum_assured,
            maximum_sum_assured: values.maximum_sum_assured,
            maturity_bonus_benefit: values.maturity_bonus_benefit,
            death_benefit: values.death_benefit,
            loan_facility: values.loan_facility,
            surrendar_value: values.surrendar_value,
            // send both spellings to handle API variations
            surrender_value: values.surrendar_value,
          };

          console.log("FINAL PAYLOAD =", withStatus(payload));
          console.log("FEATURES ID =", values.features_id);

        try{
          if (values.features_id) {
            try {
              await updateProductAdditionalInfo(values.features_id, withStatus(payload));
              validation.setFieldValue("id", values.id, false);
              validation.setFieldValue("product_id", values.id, false);
              validation.setFieldValue("features_id", values.features_id, false);
              toast.success("Features Updated!");
            } catch (error: any) {
              console.error("Update failed:", error);
              toast.error("Update API failed. Please check payload / id.");
            }
          } else {
            const res = await addProductAdditionalInfo({
              ...payload,
              product_id: values.id,  
            });
           if (res?.success || res?.status === 200) {
                validation.setFieldValue("features_id", res?.data, false);
                validation.setFieldValue("id", values.id, false);
                validation.setFieldValue("product_id", values.id, false);
                toast.success("Features Saved!");
              } else {
                toast.error(res?.message || "Unable to add product Additional info.");
              }
            }
          } catch (error: any) {
            console.error("API Error:", error);
            toast.error("Something went wrong. Please try again later.");
          }
            break;
          }


          case "Eligibility": {
          const payload = {
            product_id: values.id,
            min_age: values.min_age,
            max_age: values.max_age,
            max_maturity_age: values.max_maturity_age,
            customer_eligibility: values.customer_eligibility,
            medical_requirement: values.medical_requirement,
          };

          if (values.eligibility_id) {
            await updateProductEligibility(values.eligibility_id, withStatus(payload));
            // Ensure IDs are preserved after update
            validation.setFieldValue("id", values.id, false);
            validation.setFieldValue("product_id", values.id, false);
            validation.setFieldValue("eligibility_id", values.eligibility_id, false);
            toast.success("Eligibility Updated!");
          } else {
            const res = await addProductEligibility(payload);
            validation.setFieldValue("eligibility_id", res?.data, false);
            // Preserve product_id
            validation.setFieldValue("id", values.id, false);
            validation.setFieldValue("product_id", values.id, false);
            toast.success("Eligibility Saved!");
          }
          break;
        }

        case "Premium": {
          toast.success(isEdit ? "Product Updated Successfully!" : "Product Added Successfully!");
          toggleModal();
          break;
        }
      }
      const nextIndex = TABS.indexOf(activeTab) + 1;
      if (nextIndex < TABS.length) setActiveTab(TABS[nextIndex]);
    } catch (error) {
      console.log("ERROR:", error);
    }

    setLoading(false); 
  };

    const isPremiumValid = () => {
        if (!validation.values.premium_table?.length) return false;

        return validation.values.premium_table.every((row: any) =>
          row.age &&
          row.monthly_bd &&
          row.quarterly_bd &&
          row.half_yearly_bd &&
          row.sum_assured &&
          row.yearly_bd &&
          row.saved === true  // Must be saved!
        );
      };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className="btn"
            style={{
              borderRadius: "50px",
              padding: "6px 18px",
              backgroundColor: activeTab === tab ? "#0F5132" : "#f8f9fa",
              color: activeTab === tab ? "white" : "black",
              border: activeTab === tab ? "none" : "1px solid #ced4da",
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Basic Info" && <BasicInfoForm validation={validation} />}
      {activeTab === "Key Benefits" && (
        <KeyBenefitsForm
          validation={validation}
          setModal={undefined}
          allowDelete={allowDelete}
        />
      )}
      {activeTab === "Features" && <FeaturesForm validation={validation} />}
      {activeTab === "Eligibility" && <EligibilityForm validation={validation} />}
      {activeTab === "Premium" && (
        <PremiumForm
          validation={validation}
          allowDelete={allowDelete}
          isEdit={isEdit}
          isUpdateProductMode={!isNewProduct}
        />
      )}

      <div className="d-flex justify-content-end mt-4">
        <Button
          color="primary"
          onClick={handleStepSubmit}
          disabled={
            loading || 
            (activeTab === "Key Benefits" &&
              (!values.id || (!isEdit && !validation.values.benefits?.every((b: any) => b.saved)))
            ) ||
            (activeTab === "Premium" && !isEdit && !isPremiumValid())
          }
        >
          {loading ? (
            <span>
              <i className="fa fa-spinner fa-spin"></i> Loading...
            </span>
          ) : activeTab === "Premium" ? (
            "Submit"
          ) : activeTab === "Key Benefits" && isEdit ? (
            "Next"
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProductForm;
